import type { AnyVersionTemplate, BlockDefinition, DataSource, Issue, LibraryItem, ReportData, ReportTemplate, Theme, EditorStore, LicenseTier } from "@broadpaper/core";
import type { AsyncMeasurer, LayoutResult } from "@broadpaper/renderer";
import type { TemplateAiProvider } from "@broadpaper/core/ai";
import type { GeometryMap, Measurer, RenderTreeOptions, ResolvedNode } from "@broadpaper/core";

/**
 * What the editor needs of a measurer: the `Measurer` contract, optionally
 * asynchronous, plus two lifecycle hooks it will call when it has them.
 */
export interface EditorMeasurer {
  measure(sections: ResolvedNode[], width: number): GeometryMap | Promise<GeometryMap>;
  setOptions?(opts: RenderTreeOptions): void;
  /** Invalidates cached geometry, e.g. when a web font finishes loading. */
  clearCache?(): void;
  /**
   * Resolves once whatever the last measurements found still arriving — an image
   * not yet decoded, a face not yet loaded — has arrived, with whether there was
   * anything. The editor lays the canvas out again when there was.
   */
  settled?(): Promise<boolean>;
  destroy?(): void;
}

export interface SampleDataSet {
  id: string;
  label: string;
  description?: string;
  data: ReportData;
}

export interface FeatureFlags {
  /** Show the Export menu (PDF service / print). */
  pdfExport: boolean;
  /** Theme switcher in the toolbar. */
  themes: boolean;
  /** Show custom blocks in the palette. */
  customBlocks: boolean;
  /** Reusable library panel. */
  library: boolean;
  /** Per-block style overrides in the inspector. */
  styleOverrides: boolean;
  /** Page settings (size, margins, regions). */
  pageSettings: boolean;
  /** Document styles editor (H1..caption, table, card). */
  documentStyles: boolean;
  /** Header/footer regions editable. */
  headerFooter: boolean;
  /** Layers panel. */
  layers: boolean;
  /** Data panel. */
  dataPanel: boolean;
  /** Validation issues in the toolbar. */
  validation: boolean;
  /** JSON view of the template. */
  jsonView: boolean;
  /** Advanced expression editing (free-form expressions). */
  expressions: boolean;
  /** Hide the BroadPaper mark in the toolbar (white-label). */
  whiteLabel: boolean;
}

export const DEFAULT_FEATURES: FeatureFlags = {
  pdfExport: true,
  themes: true,
  customBlocks: true,
  library: true,
  styleOverrides: true,
  pageSettings: true,
  documentStyles: true,
  headerFooter: true,
  layers: true,
  dataPanel: true,
  validation: true,
  jsonView: true,
  expressions: true,
  whiteLabel: false
};

export interface ExportContext {
  template: ReportTemplate;
  theme: Theme;
  data: ReportData;
  dataSources: DataSource[];
  layout: LayoutResult | null;
  /** Renders the current preview through the browser print dialog. */
  print(): void;
}

export interface ReportDesignerOptions {
  /** Initial template. A new one is created when omitted. Old schema versions are migrated. */
  template?: ReportTemplate | AnyVersionTemplate;
  dataSources?: DataSource[];
  /** Sample data used by design mode and preview. One data set or several to switch between. */
  sampleData?: ReportData | SampleDataSet[];
  /** The active theme. */
  theme?: Theme;
  /** Themes offered in the switcher. The active theme is added automatically. */
  themes?: Theme[];
  /** Custom block definitions. */
  blocks?: Array<BlockDefinition<any>>;
  /** Host-provided reusable content. */
  library?: LibraryItem[];
  features?: Partial<FeatureFlags>;
  /** Licence tiers enabled for this host. */
  enabledTiers?: LicenseTier[];
  locale?: string;
  currency?: string;
  timeZone?: string;
  /** "light" | "dark" | "system". Default "light". */
  appearance?: "light" | "dark" | "system";
  /** Initial zoom (1 = 100%). Default fits the page width. */
  zoom?: number | "fit";
  /** Extra class on the root element. */
  className?: string;
  /**
   * Supplies the measurer the canvas pages with.
   *
   * The default measures in the browser, which is fast and needs nothing extra.
   * Pass a factory to measure with the same engine that renders the PDF, and
   * the canvas breaks pages exactly where the file does — worth the extra
   * milliseconds when the two disagreeing would be worse than a slower canvas.
   * The container is the element the measurer may mount into; engine-backed
   * measurers will not need it.
   */
  createMeasurer?(ctx: { document: Document; container: HTMLElement }): EditorMeasurer;

  /**
   * Whether charts respond to a pointer and to the keyboard on screen.
   *
   * The drawing does not change — the same SVG the PDF contains gains a hover,
   * a focus ring and a tooltip — so this cannot make the canvas and the file
   * disagree. "preview" is the default: design mode draws sample data, and a
   * tooltip quoting a synthesised figure reads as a real one.
   */
  interactiveCharts?: "preview" | "always" | "off";

  /**
   * Where an uploaded image goes.
   *
   * Without this the designer reads the file in the browser and stores it as a
   * `data:` URL inside the template, which needs no server and is the right
   * answer for a logo or a signature. It is the wrong answer for anything
   * large: the bytes travel with the template everywhere it goes — the host's
   * database, every save, every render request — so the built-in path refuses
   * files over `MAX_INLINE_IMAGE_BYTES` rather than quietly making documents
   * that are expensive to move around.
   *
   * Return an address the renderer can fetch and the designer stores that
   * instead, with no size limit of its own: a host with somewhere to put files
   * knows better than the designer does what belongs there. Returning null, or
   * throwing, cancels the upload — throw with a message worth reading, because
   * it is shown under the field.
   */
  onUploadImage?(file: File): string | null | Promise<string | null>;

  /**
   * Design with a model. The host supplies the provider — the call to the model
   * is theirs, and so are the key, the data and the cost — and the designer
   * builds the request, checks every answer against the same validation as a
   * hand-made template, and shows it as a proposal the person accepts or
   * discards. Without it the AI panel does not exist.
   *
   * `generate` receives a plain-data request (`TemplateAiRequest`) and returns
   * the model's raw tool input. `buildTemplateAiPrompt` from
   * `@broadpaper/core/ai` turns a request into a prompt for any provider; send
   * the request to your own back end and call the model there, so no key is
   * ever in the browser. Pass a stable object — replacing it re-renders the panel.
   */
  ai?: TemplateAiProvider;

  /** Fixed clock for previews (tests, screenshots). */
  now?: Date;
  /** PDF service for the built-in export. When absent, export falls back to the browser print dialog. */
  pdfService?: { url: string; token?: string };

  onChange?(template: ReportTemplate, info: { reason: string }): void;
  onSave?(template: ReportTemplate): void | Promise<void>;
  /**
   * One sentence describing where saving actually goes, shown next to the save
   * indicator. Only the host knows — the designer hands over a template and has
   * no idea whether that reaches a database, a file or nothing at all — and
   * saying nothing invites the user to assume the safest possible answer.
   */
  saveDescription?: string;
  /** Host-driven export. Return false to fall back to the built-in behaviour. */
  onExportPdf?(ctx: ExportContext): void | boolean | Promise<void | boolean>;
  onSelectionChange?(ids: string[]): void;
  onValidate?(issues: Issue[]): void;
  onModeChange?(mode: "design" | "preview"): void;
  onThemeChange?(theme: Theme): void;
  onReady?(api: ReportDesignerApi): void;
}

export interface ReportDesignerApi {
  getTemplate(): ReportTemplate;
  setTemplate(template: ReportTemplate | AnyVersionTemplate): void;
  getStore(): EditorStore;
  setTheme(theme: Theme): void;
  setThemes(themes: Theme[]): void;
  setSampleData(data: ReportData | SampleDataSet[]): void;
  setDataSources(dataSources: DataSource[]): void;
  registerBlock(def: BlockDefinition<any>): void;
  validate(): Issue[];
  undo(): void;
  redo(): void;
  setMode(mode: "design" | "preview"): void;
  getMode(): "design" | "preview";
  select(ids: string[]): void;
  exportPdf(): Promise<void>;
  print(): void;
  getLayout(): LayoutResult | null;
  update(options: Partial<ReportDesignerOptions>): void;
  destroy(): void;
}

/** A minimal observable holder for host options so React can subscribe to changes. */
export class OptionsStore {
  private listeners = new Set<() => void>();
  constructor(public value: ReportDesignerOptions) {}
  get(): ReportDesignerOptions {
    return this.value;
  }
  set(patch: Partial<ReportDesignerOptions>): void {
    this.value = { ...this.value, ...patch };
    for (const l of this.listeners) l();
  }
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
}

export function normaliseSampleData(sample: ReportDesignerOptions["sampleData"]): SampleDataSet[] {
  if (!sample) return [];
  if (Array.isArray(sample)) return sample;
  return [{ id: "default", label: "Sample data", data: sample }];
}
